// Hook that bundles every input a postprocessor needs into one
// memoised ExportContext.

import { useMemo } from "react";

import { useBom } from "@/lib/cabinets/useBom";
import { useNesting } from "@/lib/cabinets/useNesting";
import { useCabinetsIn, useCollection, useProject } from "@/lib/ddb-hooks";
import { indexCatalogs, partsFor } from "@/lib/cabinets/bom";
import type { EdgeBand, Hardware, Id, Material } from "@/lib/types";
import type { ExportContext } from "./types";

export interface UseExportContextState {
  ctx: ExportContext | null;
  isLoading: boolean;
  error: Error | null;
}

export function useExportContext(projectId: Id | null | undefined): UseExportContextState {
  const project    = useProject(projectId);
  const cabinets   = useCabinetsIn(projectId);
  const materials  = useCollection<Material>("materials");
  const edges      = useCollection<EdgeBand>("edge_bands");
  const hardware   = useCollection<Hardware>("hardware");
  const bom        = useBom(projectId);
  const nesting    = useNesting(projectId);

  const ctx = useMemo<ExportContext | null>(() => {
    if (!project.data || !cabinets.data || !materials.data || !edges.data || !hardware.data) return null;
    if (!bom.bom || !nesting.nesting) return null;
    const catalog = indexCatalogs(materials.data, edges.data, hardware.data);
    const parts = cabinets.data.map((cab) => ({
      cabinet: cab,
      parts: partsFor(cab, catalog),
    }));
    return {
      project:  project.data,
      cabinets: cabinets.data,
      parts,
      catalog,
      bom: bom.bom,
      nesting: nesting.nesting,
    };
  }, [project.data, cabinets.data, materials.data, edges.data, hardware.data, bom.bom, nesting.nesting]);

  const isLoading = project.isLoading || cabinets.isLoading || materials.isLoading || edges.isLoading || hardware.isLoading || bom.isLoading || nesting.isLoading;
  const error =
    (project.error as Error | null) ??
    (cabinets.error as Error | null) ??
    (materials.error as Error | null) ??
    (edges.error as Error | null) ??
    (hardware.error as Error | null) ??
    bom.error ?? nesting.error ?? null;

  return { ctx, isLoading, error };
}
